import { collection, getDocs, query, orderBy, Timestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// Fleet Inquiry Types
export interface FleetInquiry {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  fleetSize: string;
  message?: string;
  status: 'new' | 'contacted' | 'quoted' | 'closed';
  source: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  notes?: string;
}

// Job Application Types
export interface JobApplication {
  id: string;
  name: string;
  email: string;
  phone: string;
  message?: string;
  position: string;
  status: 'new' | 'reviewing' | 'interview' | 'hired' | 'declined';
  source: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  notes?: string;
}

// Newsletter Subscriber Types
export interface NewsletterSubscriber {
  id: string;
  email: string;
  status: 'active' | 'unsubscribed';
  source: string;
  createdAt: Timestamp; // API uses createdAt, not subscribedAt
  updatedAt: Timestamp;
  notes?: string;
}

// Metrics Types
export interface CRMMetrics {
  fleetInquiries: {
    total: number;
    byStatus: { [key: string]: number };
    byFleetSize: { [key: string]: number };
    recentInquiries: FleetInquiry[];
    trend: { date: string; count: number }[];
  };
  jobApplications: {
    total: number;
    byStatus: { [key: string]: number };
    recentApplications: JobApplication[];
    trend: { date: string; count: number }[];
  };
  newsletterSubscribers: {
    total: number;
    active: number;
    recentSubscribers: NewsletterSubscriber[];
    trend: { date: string; count: number }[];
  };
}

// ============= Fleet Inquiries =============

export async function getAllFleetInquiries(): Promise<FleetInquiry[]> {
  try {
    const fleetRef = collection(db, 'fleetInquiries');
    const q = query(fleetRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as FleetInquiry[];
  } catch (error) {
    console.error('Error fetching fleet inquiries:', error);
    throw error;
  }
}

export async function updateFleetInquiryStatus(
  inquiryId: string,
  status: FleetInquiry['status'],
  notes?: string
): Promise<void> {
  try {
    const inquiryRef = doc(db, 'fleetInquiries', inquiryId);
    const updateData: any = {
      status,
      updatedAt: Timestamp.now(),
    };
    if (notes !== undefined) {
      updateData.notes = notes;
    }
    await updateDoc(inquiryRef, updateData);
  } catch (error) {
    console.error('Error updating fleet inquiry:', error);
    throw error;
  }
}

// ============= Job Applications =============

export async function getAllJobApplications(): Promise<JobApplication[]> {
  try {
    const jobsRef = collection(db, 'jobApplications');
    const q = query(jobsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as JobApplication[];
  } catch (error) {
    console.error('Error fetching job applications:', error);
    throw error;
  }
}

export async function updateJobApplicationStatus(
  applicationId: string,
  status: JobApplication['status'],
  notes?: string
): Promise<void> {
  try {
    const applicationRef = doc(db, 'jobApplications', applicationId);
    const updateData: any = {
      status,
      updatedAt: Timestamp.now(),
    };
    if (notes !== undefined) {
      updateData.notes = notes;
    }
    await updateDoc(applicationRef, updateData);
  } catch (error) {
    console.error('Error updating job application:', error);
    throw error;
  }
}

// ============= Newsletter Subscribers =============

export async function getAllNewsletterSubscribers(): Promise<NewsletterSubscriber[]> {
  try {
    const subscribersRef = collection(db, 'newsletterSubscribers');
    const q = query(subscribersRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as NewsletterSubscriber[];
  } catch (error) {
    console.error('Error fetching newsletter subscribers:', error);
    throw error;
  }
}

export async function updateNewsletterSubscriberStatus(
  subscriberId: string,
  status: NewsletterSubscriber['status'],
  notes?: string
): Promise<void> {
  try {
    const subscriberRef = doc(db, 'newsletterSubscribers', subscriberId);
    const updateData: any = {
      status,
      updatedAt: Timestamp.now(),
    };
    if (notes !== undefined) {
      updateData.notes = notes;
    }
    await updateDoc(subscriberRef, updateData);
  } catch (error) {
    console.error('Error updating subscriber:', error);
    throw error;
  }
}

// ============= Metrics & Analytics =============

export async function getCRMMetrics(): Promise<CRMMetrics> {
  try {
    const [fleetInquiries, jobApplications, newsletterSubscribers] = await Promise.all([
      getAllFleetInquiries(),
      getAllJobApplications(),
      getAllNewsletterSubscribers(),
    ]);

    // Fleet Inquiries Metrics
    const fleetByStatus: { [key: string]: number } = {};
    const fleetBySize: { [key: string]: number } = {};
    const fleetByMonth: { [key: string]: number } = {};

    fleetInquiries.forEach(inquiry => {
      fleetByStatus[inquiry.status] = (fleetByStatus[inquiry.status] || 0) + 1;
      fleetBySize[inquiry.fleetSize] = (fleetBySize[inquiry.fleetSize] || 0) + 1;
      
      if (inquiry.createdAt) {
        const date = inquiry.createdAt.toDate();
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        fleetByMonth[monthKey] = (fleetByMonth[monthKey] || 0) + 1;
      }
    });

    // Job Applications Metrics
    const jobsByStatus: { [key: string]: number } = {};
    const jobsByMonth: { [key: string]: number } = {};

    jobApplications.forEach(application => {
      jobsByStatus[application.status] = (jobsByStatus[application.status] || 0) + 1;
      
      if (application.createdAt) {
        const date = application.createdAt.toDate();
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        jobsByMonth[monthKey] = (jobsByMonth[monthKey] || 0) + 1;
      }
    });

    // Newsletter Metrics
    const activeSubscribers = newsletterSubscribers.filter(s => s.status === 'active').length;
    const subscribersByMonth: { [key: string]: number } = {};

    newsletterSubscribers.forEach(subscriber => {
      if (subscriber.createdAt) {
        const date = subscriber.createdAt.toDate();
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        subscribersByMonth[monthKey] = (subscribersByMonth[monthKey] || 0) + 1;
      }
    });

    // Growth trends (last 6 months)
    const fleetTrend = buildTrend(fleetByMonth);
    const jobsTrend = buildTrend(jobsByMonth);
    const subscribersTrend = buildTrend(subscribersByMonth);

    return {
      fleetInquiries: {
        total: fleetInquiries.length,
        byStatus: fleetByStatus,
        byFleetSize: fleetBySize,
        recentInquiries: fleetInquiries.slice(0, 10),
        trend: fleetTrend,
      },
      jobApplications: {
        total: jobApplications.length,
        byStatus: jobsByStatus,
        recentApplications: jobApplications.slice(0, 10),
        trend: jobsTrend,
      },
      newsletterSubscribers: {
        total: newsletterSubscribers.length,
        active: activeSubscribers,
        recentSubscribers: newsletterSubscribers.slice(0, 10),
        trend: subscribersTrend,
      },
    };
  } catch (error) {
    console.error('Error calculating CRM metrics:', error);
    throw error;
  }
}

function buildTrend(byMonth: { [key: string]: number }): { date: string; count: number }[] {
  const trend: { date: string; count: number }[] = [];
  const now = new Date();
  
  for (let i = 5; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    trend.push({
      date: monthKey,
      count: byMonth[monthKey] || 0,
    });
  }
  
  return trend;
}

// Export status badge colors
export const STATUS_COLORS = {
  fleet: {
    new: 'blue',
    contacted: 'purple',
    quoted: 'orange',
    closed: 'green',
  },
  job: {
    new: 'blue',
    reviewing: 'purple',
    interview: 'orange',
    hired: 'green',
    declined: 'red',
  },
  newsletter: {
    active: 'green',
    unsubscribed: 'gray',
  },
};
